import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getPaymentTemplatesByProjectId,
  createPaymentTemplates,
  updatePaymentTemplates
} from '../../services/api/implementation/impl';
import Modal from '../../../shared/components/ui/Modal';
import AddPaymentTemplate from '../forms/AddPaymentTemplate';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Table from '../../../shared/components/layout/Table';
import { transformData } from '../../../super-admin/utils/transformData';
import { Plus } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';

const PaymentTemplates = ({ projectId, hideAddButton = false, showActions = true }) => {
  const userId = Cookies.get('userId');
  const [isOpenTemplateModal, setIsOpenTemplateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditTemplate, setIsEditTemplate] = useState(false);

  // Fetch payment templates
  const {
    data: paymentTemplates,
    refetch: refetchTemplates,
    error: templatesError
  } = useQuery({
    queryKey: ['payment_templates', projectId],
    queryFn: () => getPaymentTemplatesByProjectId(projectId),
    enabled: !!projectId,
    onError: (error) => {
      console.error('Error fetching payment templates:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to fetch payment templates'
      );
    }
  });

  // Track page view
  React.useEffect(() => {
    mixpanel.track('Payment Templates Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { mutate: handleCreateTemplate } = useMutation({
    mutationFn: (payload) => createPaymentTemplates(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment template created successfully');
      setIsOpenTemplateModal(false);
      setSelectedTemplate(null);
      setIsEditTemplate(false);
      refetchTemplates();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error creating payment template:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error creating payment template'
      );
    }
  });

  const { mutate: handleUpdateTemplate } = useMutation({
    mutationFn: ({ payload, id }) => updatePaymentTemplates(payload, id),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment template updated successfully');
      setIsOpenTemplateModal(false);
      setSelectedTemplate(null);
      setIsEditTemplate(false);
      refetchTemplates();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error updating payment template:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error updating payment template'
      );
    }
  });

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setIsEditTemplate(true);
    setIsOpenTemplateModal(true);
  };

  const templateFormat = ['id', 'templateName', 'percentage', 'duration', 'intervals', 'actions'];

  const handleTemplateFormat = (item) => {
    // Calculate total percentage from intervals
    const totalPercentage =
      item.intervals?.reduce((acc, curr) => acc + (parseFloat(curr.percentage) || 0), 0) || 0;

    // Calculate total duration from intervals
    const totalDuration =
      item.intervals?.reduce((acc, curr) => acc + (parseFloat(curr.duration) || 0), 0) || 0;

    return {
      id: item.id,
      templateName: item.templateName,
      percentage: `${totalPercentage}%`,
      duration: `${totalDuration} days`,
      intervals: item.intervals?.length || 0
    };
  };

  const templateData =
    paymentTemplates?.map((item) => {
      const formatted = handleTemplateFormat(item);
      return transformData(formatted, templateFormat);
    }) || [];

  return (
    <>
      {/* Template Modal */}
      {isOpenTemplateModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={isEditTemplate ? 'Edit Payment Template' : 'Create Payment Template'}
          width={55}
          onClose={() => {
            setIsOpenTemplateModal(false);
            setSelectedTemplate(null);
            setIsEditTemplate(false);
          }}>
          <AddPaymentTemplate
            isSubmitting={isSubmitting}
            projectId={projectId}
            handleOnSubmit={
              isEditTemplate
                ? (payload) => handleUpdateTemplate({ payload, id: selectedTemplate.id })
                : handleCreateTemplate
            }
            onClose={() => {
              setIsOpenTemplateModal(false);
              setSelectedTemplate(null);
              setIsEditTemplate(false);
            }}
            existingTemplate={selectedTemplate}
          />
        </Modal>
      )}

      {/* Main Content */}
      {templatesError ? (
        <div className="flex justify-center h-[20rem]">No Data Found</div>
      ) : (
        <div className="table-container h-[calc(100vh-10rem)] overflow-auto">
          <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
          <Table
            headers={[
              'Template Name',
              'Total Percentage',
              'Total Duration',
              'Number of Intervals',
              ...(showActions ? ['Actions'] : [])
            ]}
            data={templateData}
            identifier={'id'}
            isEditEnable={showActions}
            onEdit={(id) => {
              const template = paymentTemplates?.find((t) => t.id === parseInt(id));
              if (template) handleEditTemplate(template);
            }}
            isDeleteEnable={false}
            isMarkEnable={false}
            isWrap={true}
          />
        </div>
      )}

      {/* Floating Add Button */}
      {!hideAddButton && (
        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={() => {
              mixpanel.track('Add Template Button Clicked', {
                user_id: userId,
                timestamp: new Date().toISOString()
              });
              setIsOpenTemplateModal(true);
            }}
            customStyle={{
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>
      )}
    </>
  );
};

export default PaymentTemplates;
